import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Maximize2, Mail } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import ProjectCard from "@/components/ProjectCard";
import { company } from "@/data/site";
import { useT, useCategoryName, useProjects } from "@/i18n";

export default function ProjectDetail() {
  const { slug } = useParams();
  const t = useT();
  const categoryName = useCategoryName();
  const { projects, getProject } = useProjects();
  const project = getProject(slug);
  const [lb, setLb] = useState(-1);

  if (!project) {
    return (
      <div className="pt-[200px] pb-32 text-center container-kyt" data-testid="project-not-found">
        <h1 className="font-display text-3xl font-bold text-ink">{t("detail.notFound")}</h1>
        <Link to="/projeler" className="mt-6 inline-flex items-center gap-2 text-brand-blue font-medium">
          <ArrowLeft className="h-4 w-4" /> {t("detail.backAll")}
        </Link>
      </div>
    );
  }

  const idx = projects.findIndex((p) => p.slug === slug);
  const prev = projects[(idx - 1 + projects.length) % projects.length];
  const next = projects[(idx + 1) % projects.length];
  const related = projects.filter((p) => p.category === project.category && p.slug !== slug).slice(0, 3);

  const specs = [
    { label: t("detail.specCompany"), value: project.firma },
    { label: t("detail.specValue"), value: project.bedel },
    { label: t("detail.specScope"), value: project.is },
    { label: t("detail.specDate"), value: project.tarih },
    { label: t("detail.specLocation"), value: project.location },
    { label: t("detail.specCategory"), value: categoryName(project.category) },
  ];

  const mailHref = `mailto:${company.email}?subject=${encodeURIComponent(
    t("detail.mailSubject", { name: project.name })
  )}&body=${encodeURIComponent(t("detail.mailBody", { name: project.name }))}`;

  return (
    <div data-testid="project-detail-page">
      <PageHeader title={project.name} crumb={t("detail.crumb")} image={project.cover} />

      <section className="py-16 md:py-24 bg-paper">
        <div className="container-kyt grid lg:grid-cols-3 gap-12 lg:gap-16">
          {/* GALLERY */}
          <div className="lg:col-span-2">
            <Reveal>
              <button onClick={() => setLb(0)} data-testid="main-image" className="group relative block w-full overflow-hidden aspect-[16/10] bg-navy">
                <img src={project.images[0]} alt={project.name} className="h-full w-full object-cover transition-transform duration-[1100ms] group-hover:scale-[1.04]" />
                <span className="absolute top-4 right-4 h-10 w-10 grid place-items-center bg-white/90 text-ink opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="h-5 w-5" />
                </span>
              </button>
            </Reveal>

            {project.images.length > 1 && (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-4" data-testid="gallery-thumbs">
                {project.images.slice(1).map((img, i) => (
                  <button key={img} onClick={() => setLb(i + 1)} className="group relative overflow-hidden aspect-square bg-navy" data-testid={`thumb-${i}`}>
                    <img src={img} alt={`${project.name} ${i + 2}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* KÜNYE */}
          <div className="lg:col-span-1">
            <Reveal delay={100}>
              <div className="lg:sticky lg:top-28">
                <div className="eyebrow mb-4">{t("detail.specsTitle")}</div>
                <h2 className="font-display text-2xl font-bold text-ink leading-tight">{project.name}</h2>
                <div className="mt-8">
                  {specs.map((s) => (
                    <div key={s.label} className="py-4 border-t border-ink/10">
                      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">{s.label}</div>
                      <div className="text-[15px] text-ink mt-1.5 leading-snug">{s.value}</div>
                    </div>
                  ))}
                </div>
                <a href={mailHref} data-testid="project-quote" className="mt-8 inline-flex items-center gap-3 rounded-full border border-ink/15 pl-5 pr-6 py-3.5 text-[14.5px] font-medium text-ink hover:bg-ink hover:text-white transition-colors duration-300">
                  <Mail className="h-5 w-5 text-brand-blue" /> {t("detail.inquire")}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PREV / NEXT */}
      <section className="border-y border-ink/10 bg-white">
        <div className="container-kyt grid grid-cols-2 divide-x divide-ink/10">
          <Link to={`/projeler/${prev.slug}`} className="group py-7 pr-4 flex items-center gap-4" data-testid="prev-project">
            <ArrowLeft className="h-5 w-5 text-slate-300 group-hover:text-ink transition-colors shrink-0" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">{t("detail.prev")}</div>
              <div className="text-sm text-ink line-clamp-1 mt-0.5">{prev.name}</div>
            </div>
          </Link>
          <Link to={`/projeler/${next.slug}`} className="group py-7 pl-4 flex items-center justify-end gap-4 text-right" data-testid="next-project">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">{t("detail.next")}</div>
              <div className="text-sm text-ink line-clamp-1 mt-0.5">{next.name}</div>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-ink transition-colors shrink-0" />
          </Link>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-20 md:py-28 bg-paper">
          <div className="container-kyt">
            <div className="flex items-end justify-between mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink">{t("detail.related")}</h2>
              <Link to={`/projeler?kategori=${project.category}`} className="text-sm font-medium text-ink inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
                {t("detail.allInCategory")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 70}>
                  <ProjectCard project={p} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {lb >= 0 && (
        <Lightbox images={project.images} index={lb} title={project.name} onClose={() => setLb(-1)} onIndex={setLb} />
      )}
    </div>
  );
}
